/**
 * The storefront's Best Available search, as one function, so the panel that
 * runs it for the buyer and the hold-time retry that re-runs it after a
 * collision send the SAME request and read the SAME response.
 */

export interface BestAvailableSearch {
  quantity: number
  categoryId?: string
  /** A focused section scopes the search; the search widens to the venue when the scope has no match. */
  sectionKey?: string
  sortBy: 'best' | 'price' | 'location'
  focalPoint?: { x: number; y: number } | null
}

export interface BestAvailableSeat {
  seatId: string
  seatNumber: string
  seatIndex: number
  rowLabel: string
  rowNumber: number
  sectionKey: string
  sectionName: string
  categoryId: string | null
  price: number | null
}

export interface BestAvailableOption {
  seats: BestAvailableSeat[]
  score: number
  warning?: string
}

/**
 * Ranked options for the search, best first; `[]` when nothing matches. The
 * scope that produced the result is reported back so a retry can repeat it
 * exactly (`usedSectionKey` is undefined when the search had to widen).
 */
export async function searchBestAvailable(
  eventId: string,
  search: BestAvailableSearch,
  fetchImpl: typeof fetch = fetch
): Promise<{ options: BestAvailableOption[]; usedSectionKey?: string; error?: string }> {
  // The server's reason for a miss ("sold as whole tables", "general
  // admission"), so the panel can say it rather than a generic "No seats".
  let error: string | undefined
  const run = async (scope: string | undefined): Promise<BestAvailableOption[]> => {
    const res = await fetchImpl(`/api/events/${eventId}/seats/best-available`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        quantity: search.quantity,
        categoryId: search.categoryId || undefined,
        sectionKey: scope,
        consecutiveRequired: true,
        sortBy: search.sortBy,
        focalPoint: search.sortBy === 'location' ? search.focalPoint : undefined,
        topN: 5,
      }),
    })
    const data = await res.json().catch(() => null)
    error = !data?.success && typeof data?.error === 'string' ? data.error : undefined
    return data?.success && Array.isArray(data.data?.results) ? data.data.results : []
  }
  // A focused section silently scopes the search — when that scope has no
  // match, widen to the whole venue in the SAME call. (A scoped miss used to
  // show a tiny error and only work on the second click.)
  const scoped = search.sectionKey || undefined
  let options = await run(scoped)
  if (options.length > 0) return { options, usedSectionKey: scoped }
  if (scoped) options = await run(undefined)
  return { options, usedSectionKey: undefined, error: options.length > 0 ? undefined : error }
}
